import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle, Send } from 'lucide-react';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-6 animate-fadeIn">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/edassist-logo.png" alt="EdAssist" className="h-10" />
          </Link>
        </div>

        {sent ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center space-y-4" data-testid="reset-sent-confirmation">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Check your email
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
              The link expires in 24 hours.
            </p>
            <div className="pt-2 space-y-3">
              <Button variant="outline" className="w-full" onClick={() => { setSent(false); setEmail(''); }} data-testid="try-another-email-btn">
                Try another email
              </Button>
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full text-indigo-600">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-8 space-y-6" data-testid="forgot-password-form">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Forgot your password?
              </h1>
              <p className="text-slate-600 text-sm">
                Enter your email and we'll send you a link to reset your password.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-11" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send reset link</>}
              </Button>
            </form>

            <Link to="/login" className="block text-center">
              <Button variant="ghost" className="text-indigo-600 text-sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
